import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { CalendarClock, CalendarDays, Check, ExternalLink, ListChecks, Loader2, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import { api, useAuth } from '../context/AuthContext';
import { Toaster } from '../components/ui/sonner';
import { RueckrufSchnell, RueckrufVersuche } from '../components/kanzlei/RueckrufSchnell';

const VOLL = ['admin', 'kanzlei_mitarbeiter'];
const ART = {
  aufgabe: { label: 'Aufgabe', icon: ListChecks, ton: 'text-[#1B3A5C] bg-[#1B3A5C]/5 border-[#1B3A5C]/15', link: '/team/tickets' },
  rueckruf: { label: 'Rückruf', icon: PhoneCall, ton: 'text-[#7a5a22] bg-[#e0b56a]/15 border-[#e0b56a]/40', link: '/team/rueckrufe' },
  termin: { label: 'Termin', icon: CalendarDays, ton: 'text-emerald-800 bg-emerald-50 border-emerald-200', link: '/kanzlei/termine' },
};
const FILTER = [['alle', 'Alle'], ['aufgabe', 'Aufgaben'], ['rueckruf', 'Rückrufe'], ['termin', 'Termine']];
const plus = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmt = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });

const gruppen = (items, heute) => {
  const woche = plus(heute, 7);
  const g = [['ueberfaellig', 'Überfällig', []], ['heute', 'Heute', []], ['woche', 'Nächste 7 Tage', []], ['spaeter', 'Später', []], ['ohne', 'Ohne Datum', []]];
  const idx = (i) => (!i.datum ? 4 : i.datum < heute ? 0 : i.datum === heute ? 1 : i.datum <= woche ? 2 : 3);
  items.forEach((i) => g[idx(i)][2].push(i));
  return g.filter(([, , l]) => l.length);
};

const Zeile = ({ it, heute, onErledigt, onVersuch }) => {
  const a = ART[it.art];
  const I = a.icon;
  const [busy, setBusy] = useState(false);
  const spaet = it.datum && it.datum < heute;
  const erledigen = async () => {
    setBusy(true);
    try { await api.patch(`/team/tickets/${it.id}`, { status: 'erledigt' }); onErledigt(it.id); toast.success('Aufgabe erledigt'); } catch { toast.error('Konnte nicht gespeichert werden'); setBusy(false); }
  };
  return (
    <li className={`group flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 rounded-xl border bg-white transition-[box-shadow,transform] duration-200 hover:shadow-md hover:-translate-y-px ${spaet ? 'border-rose-200 border-l-4 border-l-rose-400' : 'border-gray-100'}`} data-testid={`planer-item-${it.art}-${it.id}`}>
      <span className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase px-2.5 py-1 rounded-full border ${a.ton}`}><I className="w-3.5 h-3.5" />{a.label}</span>
      <span className="flex-1 min-w-[180px]">
        <span className="block text-sm text-[#1B3A5C] font-medium">{it.titel}{it.wichtig && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">{it.art === 'rueckruf' ? 'dringend' : 'wichtig'}</span>}{it.art === 'rueckruf' && <span className="ml-2"><RueckrufVersuche versuche={it.versuche} /></span>}</span>
        <span className="block text-xs text-[#5a7184]">{[it.mandant && it.art !== 'rueckruf' ? it.mandant : null, it.info, it.zustaendig ? `zuständig: ${it.zustaendig}` : 'nicht zugewiesen'].filter(Boolean).join(' · ')}</span>
      </span>
      <span className={`text-xs font-mono ${spaet ? 'text-rose-700' : 'text-[#34536b]'}`} data-testid={`planer-wann-${it.id}`}>
        {it.status === 'nachtragen' ? 'Status nachtragen · ' : ''}{it.datum ? fmt(it.datum) : '—'}{it.uhrzeit ? ` · ${it.uhrzeit} Uhr` : ''}
      </span>
      <span className="flex items-center gap-1.5">
        {it.art === 'aufgabe' && (
          <button type="button" disabled={busy} onClick={erledigen} className="inline-flex items-center gap-1 text-[11px] tracking-wider px-3 py-1.5 rounded-full border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors duration-200" data-testid={`planer-erledigt-${it.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} ERLEDIGT
          </button>
        )}
        {it.art === 'rueckruf' && <RueckrufSchnell it={it} onErledigt={onErledigt} onVersuch={onVersuch} />}
        <Link to={a.link} className="inline-flex items-center gap-1 text-[11px] tracking-wider px-3 py-1.5 rounded-full border border-gray-200 text-[#1B3A5C] hover:border-[#b8893d] transition-colors duration-200" data-testid={`planer-oeffnen-${it.id}`}>
          <ExternalLink className="w-3 h-3" /> ÖFFNEN
        </Link>
      </span>
    </li>
  );
};

export default function KanzleiPlaner() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [art, setArt] = useState('alle');
  const [meine, setMeine] = useState(false);
  const laden = useCallback(() => api.get('/team/planer', { params: { nur_meine: meine } }).then((r) => setD(r.data)).catch(() => setD({ items: [], heute: new Date().toISOString().slice(0, 10), fehler: true })), [meine]);
  useEffect(() => { laden(); }, [laden]);
  const liste = useMemo(() => (d?.items || []).filter((i) => art === 'alle' || i.art === art), [d, art]);
  if (user && !VOLL.includes(user.role)) return <Navigate to="/kanzlei/eingang" replace />;
  const zahl = (k) => (d?.items || []).filter((i) => k === 'alle' || i.art === k).length;
  const ueberfaellig = (d?.items || []).filter((i) => i.datum && i.datum < d.heute).length;
  return (
    <div className="min-h-screen bg-[#f5f8fa]" data-testid="planer-page">
      <Header />
      <main className="pt-[136px] lg:pt-[184px] pb-16 px-4 max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#1B3A5C]">Heute & diese Woche</h1>
            <p className="text-sm text-[#34536b] mt-1">Aufgaben, Rückrufe und Termine nach Fälligkeit{ueberfaellig ? <span className="text-rose-700" data-testid="planer-ueberfaellig-zahl"> · {ueberfaellig} überfällig</span> : null}</p>
          </div>
          <button type="button" onClick={() => setMeine((v) => !v)} className={`text-xs tracking-wider px-3 py-1.5 rounded-full border transition-colors duration-200 ${meine ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-[#1B3A5C] border-gray-200 hover:border-[#b8893d]'}`} data-testid="planer-nur-meine">Nur meine</button>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-6">
          {FILTER.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setArt(k)} data-testid={`planer-filter-${k}`}
              className={`text-xs tracking-wider px-3 py-1.5 rounded-full border transition-colors duration-200 ${art === k ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-[#1B3A5C] border-gray-200 hover:border-[#b8893d]'}`}>{l} <span className="opacity-60">{d ? zahl(k) : ''}</span></button>
          ))}
        </div>
        {!d ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#b8893d]" /></div>
          : d.fehler ? <p className="text-sm text-rose-700" data-testid="planer-fehler">Die Übersicht konnte nicht geladen werden.</p>
          : !liste.length ? <p className="text-sm text-[#5a7184] py-12 text-center" data-testid="planer-leer">Nichts offen – alles erledigt.</p>
          : gruppen(liste, d.heute).map(([k, titel, l]) => (
            <section key={k} className="mb-8" data-testid={`planer-gruppe-${k}`}>
              <h2 className={`text-xs font-semibold tracking-[0.18em] uppercase mb-3 ${k === 'ueberfaellig' ? 'text-rose-700' : 'text-[#8a6520]'}`}>{titel} <span className="opacity-70">({l.length})</span></h2>
              <ul className="space-y-2">{l.map((it) => <Zeile key={`${it.art}-${it.id}`} it={it} heute={d.heute}
                onErledigt={(id) => setD((x) => ({ ...x, items: x.items.filter((i) => !(i.art === it.art && i.id === id)) }))}
                onVersuch={(id, v) => setD((x) => ({ ...x, items: x.items.map((i) => (i.art === 'rueckruf' && i.id === id ? { ...i, versuche: [...(i.versuche || []), v] } : i)) }))} />)}</ul>
            </section>
          ))}
      </main>
      <Toaster />
    </div>
  );
}
